import { Button, createStyles, Dialog, DialogTitle, IconButton, makeStyles, Theme, Tooltip, Typography } from "@material-ui/core";
import * as React from "react";
import { useGameContext } from "../../../GameContext/GameContext";
import { getOriginalGamePowerupImageName, OriginalGamePowerup, OriginalGamePowerupMode, OriginalGameState } from "../../../GameContext/OriginalGameState";
import * as ThemeConstants from "../../../../Styles/Constants";
import { useWindowDimensions } from "../../UseWindowDimensions";
import { LetterGrid } from "../LetterGrid";
import clsx from "clsx";
import { SocketMessageType } from "../../../../Definitions/Definitions";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            marginTop: "1rem"
        },
        button: {
            transition: "background-color 200ms ease 0s"
        },
        secondaryButton: {
            transform: "translate(-35%, -50%)",
            zIndex: -1
        },
        dialogContent: {
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: "1rem",
            marginBottom: "1rem",
            overflowY: "auto"
        },
        playerButton: {
            minWidth: "60%"
        },
        notifListContainer: {
            position: "fixed",
            top: "40%",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "black",
            pointerEvents: "none",
            maxHeight: "96px",
            overflowY: "hidden",
            textAlign: "center",
            transition: "opacity 200ms ease 0s",
            zIndex: 20,
            [theme.breakpoints.up('xs')]: {
                maxHeight: "84px",
                minHeight: "28px"
            },
            [theme.breakpoints.up('sm')]: {
                maxHeight: "96px",
                minHeight: "32px"
            },
            [theme.breakpoints.up('md')]: {
                maxHeight: "96px",
                minHeight: "32px"
            },
            [theme.breakpoints.up('lg')]: {
                maxHeight: "96px",
                minHeight: "32px"
            }
        },
        eventText: {
            opacity: 0,
            animation: `$eventEffect 2000ms ${theme.transitions.easing.easeInOut}`,
        },
        "@keyframes eventEffect": {
            "0%": {
                opacity: 0
            },
            "20%": {
                opacity: 1
            },
            "80%": {
                opacity: 1
            },
            "100%": {
                opacity: 0
            }
        }
    })
);

export const OriginalGamePowerupButton = () => {
    const classes = useStyles();
    const { width, height } = useWindowDimensions();
    const gameContext = useGameContext();

    const {
        localPlayer, actualLocalPlayer, gameResults, playerList, playerStateList,
        gameStarted, gameParameters,
        currentPowerup, lastPowerupReset, recentPowerupEvents
    } = gameContext.gameState as OriginalGameState;

    const [powerupTimeRemaining, setPowerupTimeRemaining] = React.useState(gameParameters.powerupDelay);
    const [timerCounter, setTimerCounter] = React.useState(0);
    const [overlayPowerup, setOverlayPowerup] = React.useState(currentPowerup);
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const timerActive = powerupTimeRemaining > 0;

    const usePowerup = (target: string) => {
        gameContext.sendMessage(SocketMessageType.PowerupUsed, target);
        setOverlayPowerup(OriginalGamePowerup.None);
        setDialogOpen(false);
    }

    const powerupClicked = () => {
        if (timerActive || overlayPowerup == OriginalGamePowerup.None) return;
        if (actualLocalPlayer?.isSpectator || !localPlayer) return;

        // either open target select or just send the message depending on the powerup
        switch (overlayPowerup) {
            default: return;
            case OriginalGamePowerup.ColorFlip:
            case OriginalGamePowerup.HideGuess:
                setDialogOpen(true);
                return;

            case OriginalGamePowerup.Hint:
            case OriginalGamePowerup.ScoreMultiplier:
                usePowerup(localPlayer.id);
                return;
        }
    }

    React.useEffect(() => {
        if (gameStarted && gameParameters.activePowerupMode != OriginalGamePowerupMode.Disabled) {
            const timer = setTimeout(() => {
                const utcTimestampMS = Date.parse((new Date()).toUTCString());
                const resetOffset = Math.round((utcTimestampMS - lastPowerupReset) / 1000);
                const newTime = Math.max(gameParameters.powerupDelay - resetOffset, 0);

                if (newTime > 0)
                    setOverlayPowerup(currentPowerup);

                setTimerCounter(timerCounter + 1);
                setPowerupTimeRemaining(newTime);

                // clear out powerups that have passed their duration
                gameContext.updateGameState((prevState: OriginalGameState) => {
                    const utcTimestampMS = Date.parse((new Date()).toUTCString());
                    const newActive = prevState.activePowerups.filter((e) => {
                        const timeDiff = (utcTimestampMS - e.timestamp) / 1000;
                        return timeDiff < e.duration;
                    });

                    return {
                        ...prevState,
                        activePowerups: newActive
                    };
                });
            }, 250);

            return () => clearTimeout(timer);
        }
    }, [gameStarted, timerCounter]);

    React.useEffect(() => {
        // ensure the correct powerup is displayed when switching between spectators
        setOverlayPowerup(currentPowerup);
    }, [localPlayer])

    return (
        <div className={classes.root} style={{ marginLeft: width < 600 ? "" : "2rem", marginRight: (width >= 600 && width < 900) ? (width - 600) / 3.5 : "" }}>
            <Tooltip title={<Typography variant="body1">Activate powerup</Typography>}>
                <IconButton
                    className={classes.button}
                    style={{
                        backgroundColor: (overlayPowerup == OriginalGamePowerup.None || timerActive) ? "#b7b4ae21" : "#b7b4aeff",
                        minWidth: width < 600 ? 56 : 70,
                        minHeight: width < 600 ? 56 : 70,
                    }}
                    onClick={powerupClicked}
                >
                    {overlayPowerup != OriginalGamePowerup.None && (
                        timerActive ?
                        <Typography variant="h4">{powerupTimeRemaining}</Typography>
                        :
                        <img src={`images/powerups/${getOriginalGamePowerupImageName(overlayPowerup)}`} width={width < 600 ? 32 : 46} />
                    )}
                </IconButton>
            </Tooltip>
            <Dialog open={dialogOpen} fullWidth maxWidth={'sm'} onClose={() => setDialogOpen(false)}>
                <DialogTitle>Select a target</DialogTitle>
                <div className={classes.dialogContent}>
                    {
                        // sort the player list by name so it doesn't shift around when a new guess comes in
                        [...playerList].sort((a, b) => { if (a.name == b.name) return 0; return a.name < b.name ? -1 : 1 }).map((p, i) => {
                            // do not include player if they have completed all words
                            if (p.id in playerStateList && playerStateList[p.id].wordIndex >= gameParameters.wordCount) return;
                            if (p.isSpectator) return;

                            return (
                                <Button key={i} className={classes.playerButton} variant="outlined" onClick={() => usePowerup(p.id)}>
                                    {p.name}
                                    {(localPlayer && p.id == localPlayer.id) &&
                                        <Typography variant="body1" style={{ color: ThemeConstants.PALETTE_ORANGE, marginLeft: "0.5rem" }}>(YOU)</Typography>
                                    }
                                </Button>
                            );
                        })
                    }
                </div>
                <Button variant="contained" color="secondary" onClick={() => setDialogOpen(false)}>Close</Button>
            </Dialog>
            <div className={classes.notifListContainer} style={{ width: width < 600 ? "100vw" : "60vw", opacity: recentPowerupEvents.length > 0 ? 0.5 : 0.0 }}>
                {
                    [...recentPowerupEvents].reverse().map((e, i) => {
                        return (
                            <Typography key={e.id} className={classes.eventText} variant="h6">{e.message}</Typography>
                        );
                    })
                }
            </div>
        </div>
    );
}